import {
  BASE_BREAKDOWN_OPTIONS,
  REPORT_PREVIEW_AGGREGATE_BY_TYPES,
} from '@/models/dashboards/aggregate-type.enum';
import { REPORT_CATEGORY_OPTIONS } from '@/app/dashboards/utils/reports.enum';

// Types
export interface BreakdownConfig {
  secondary_breakdown_options: string[];
}

export interface BreakdownOptions {
  [key: string]: BreakdownConfig;
}

export interface AggregationOption {
  value: string;
  label: string;
  text?: string;
  icon?: string;
  formattedValue?: string;
  hideByVisualizationTypes?: string[];
  [key: string]: unknown | undefined;
}

export enum VISUALIZATION_OPTIONS {
  DOUGHNUT_CHART = 'DOUGHNUT_CHART',
  GRAPH = 'GRAPH',
  HORIZONTAL_BAR_CHART = 'HORIZONTAL_BAR_CHART',
  LINE_CHART = 'LINE_CHART',
  SINGLE_BAR_GRAPH = 'SINGLE_BAR_GRAPH',
  STACKED_BAR_GRAPH = 'STACKED_BAR_GRAPH',
  TABLE = 'TABLE',
  TOTAL = 'TOTAL',
  VERTICAL_BAR_CHART = 'VERTICAL_BAR_CHART',
}

// Map backend aggregation keys to enum objects for different report categories
const PRIMARY_AGGREGATION_MAP: Record<string, AggregationOption> = Object.freeze({
  TOTAL: REPORT_PREVIEW_AGGREGATE_BY_TYPES.TOTAL,
  AGE_GENDER: REPORT_PREVIEW_AGGREGATE_BY_TYPES.AGE_GENDER,
  AGE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.AGE,
  ASSIGNEE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.ASSIGNEE,
  BRAND: REPORT_PREVIEW_AGGREGATE_BY_TYPES.BRAND,
  CHANNEL: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CHANNEL,
  CITY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CITY,
  COUNTRY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.COUNTRY,
  GENDER: REPORT_PREVIEW_AGGREGATE_BY_TYPES.GENDER,
  INDUSTRY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.INDUSTRY,
  MEDIA_TYPE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MEDIA_TYPE,
  MENTION_TYPE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MENTION_TYPE,
  MESSAGE_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MESSAGE_TAG,
  MESSAGE_TYPE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MESSAGE_TYPE,
  PUBLISHER_PLATFORM: REPORT_PREVIEW_AGGREGATE_BY_TYPES.PUBLISHER_PLATFORM,
  REGION: REPORT_PREVIEW_AGGREGATE_BY_TYPES.REGION,
  SENTIMENT: REPORT_PREVIEW_AGGREGATE_BY_TYPES.SENTIMENT,
  SOURCE_CAMPAIGN: REPORT_PREVIEW_AGGREGATE_BY_TYPES.SOURCE_CAMPAIGN,
  STATE_PROVINCE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.STATE_PROVINCE,
  TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.TAG,
  TOPIC: REPORT_PREVIEW_AGGREGATE_BY_TYPES.TOPIC,
});

// Map backend secondary breakdown keys to enum objects
const SECONDARY_BREAKDOWN_MAP: Record<string, AggregationOption> = Object.freeze({
  NONE: BASE_BREAKDOWN_OPTIONS.NONE,
  AGE: BASE_BREAKDOWN_OPTIONS.AGE,
  ASSIGNEE: BASE_BREAKDOWN_OPTIONS.ASSIGNEE,
  BRAND: BASE_BREAKDOWN_OPTIONS.BRAND,
  CHANNEL: BASE_BREAKDOWN_OPTIONS.CHANNEL,
  CITY: BASE_BREAKDOWN_OPTIONS.CITY,
  COUNTRY: BASE_BREAKDOWN_OPTIONS.COUNTRY,
  GENDER: BASE_BREAKDOWN_OPTIONS.GENDER,
  INDUSTRY: BASE_BREAKDOWN_OPTIONS.INDUSTRY,
  MEDIA_TYPE: BASE_BREAKDOWN_OPTIONS.MEDIA_TYPE,
  MENTION_TYPE: BASE_BREAKDOWN_OPTIONS.MENTION_TYPE,
  MESSAGE_TYPE: BASE_BREAKDOWN_OPTIONS.MESSAGE_TYPE,
  SENTIMENT: BASE_BREAKDOWN_OPTIONS.SENTIMENT,
  STATE_PROVINCE: BASE_BREAKDOWN_OPTIONS.STATE_PROVINCE,
  TOPIC: BASE_BREAKDOWN_OPTIONS.TOPIC,
});

export const categoryByVisualizationType: Record<string, VISUALIZATION_OPTIONS[]> = {
  [REPORT_CATEGORY_OPTIONS.OWNED.id]: [
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.SINGLE_BAR_GRAPH,
    VISUALIZATION_OPTIONS.STACKED_BAR_GRAPH,
  ],
  [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: [
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  ],
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: [
    VISUALIZATION_OPTIONS.HORIZONTAL_BAR_CHART,
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  ],
  [REPORT_CATEGORY_OPTIONS.TSI.id]: [
    VISUALIZATION_OPTIONS.DOUGHNUT_CHART,
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  ],
};

const CATEGORIES_WITHOUT_GRAPH_AND_LINE_CHART_OPTIONS: string[] = [
  REPORT_CATEGORY_OPTIONS.OWNED.id,
  REPORT_CATEGORY_OPTIONS.COMMUNITY.id,
  REPORT_CATEGORY_OPTIONS.TSI.id,
];

/**
 * Get list of available primary aggregation keys from backend breakdown options
 */
export function getAvailablePrimaryAggregations(
  breakdownOptions: BreakdownOptions | null | undefined,
): string[] {
  if (!breakdownOptions || typeof breakdownOptions !== 'object') {
    return [];
  }

  return Object.keys(PRIMARY_AGGREGATION_MAP).filter((key) =>
    Object.keys(breakdownOptions).includes(key),
  );
}

/**
 * Build primary aggregation options from backend breakdown options
 * Maps backend keys to full enum objects with labels, icons, etc.
 */
export function buildPrimaryAggregationOptions(
  breakdownOptions: BreakdownOptions | null | undefined,
): AggregationOption[] {
  const primaryKeys = getAvailablePrimaryAggregations(breakdownOptions);

  if (primaryKeys.length === 0) {
    return [];
  }

  const mappedOptions = primaryKeys
    .map((key) => {
      const enumObject = PRIMARY_AGGREGATION_MAP[key];
      return enumObject;
    })
    .filter(Boolean); // Remove undefined values

  if (mappedOptions.length === 0) {
    return [];
  }

  return mappedOptions;
}

// metrics objects and breakdown options from backend
export function buildNestedVisualizationOptionsByCategory(
  breakdownOptions: BreakdownOptions | null | undefined,
  categoryVisualizationMap: Record<string, string[]> = categoryByVisualizationType,
) {
  /*
  return format
    {
      community_report: {
        TOTAL: {
          items: null,
        },
        LINE_CHART: {
          items: null,
        },
        TABLE: {
          items: [
            { value: 'BRAND', label: 'by Brand', formattedValue: 'Brand' },
            { value: 'CHANNEL', label: 'by Channel', formattedValue: 'Channel' },
            ...
          ]
        },
        VERTICAL_BAR_CHART: {
          items: [...]
        }
      },
      tsi_report: {
        TOTAL: { items: null },
        LINE_CHART: { items: null },
        ...
      },
      ...
    }
  */

  if (!breakdownOptions) {
    return {};
  }

  const result: Record<string, Record<string, { items: AggregationOption[] | null }>> = {};

  // Get primary aggregation options from backend
  const primaryAggregations = buildPrimaryAggregationOptions(breakdownOptions);

  // Build for each report category
  Object.entries(categoryVisualizationMap).forEach(([categoryId, visualizationTypes]) => {
    result[categoryId] = {};

    // Build for each visualization option within this category
    visualizationTypes.forEach((visualizationType) => {
      // TOTAL and LINE_CHART (in categories without graph and line chart options) don't have breakdown options (items: null)
      if (
        visualizationType === VISUALIZATION_OPTIONS.TOTAL ||
        (CATEGORIES_WITHOUT_GRAPH_AND_LINE_CHART_OPTIONS.includes(categoryId) &&
          (visualizationType === VISUALIZATION_OPTIONS.LINE_CHART ||
            visualizationType === VISUALIZATION_OPTIONS.GRAPH ||
            visualizationType === VISUALIZATION_OPTIONS.STACKED_BAR_GRAPH))
      ) {
        result[categoryId][visualizationType] = {
          items: null,
        };
      } else {
        // Filter out primary aggregations that are not available for the visualization type
        // For example, AGE and GENDER are not available for LINE_CHART and GRAPH visualization types
        const filteredPrimaryAggregations = primaryAggregations.filter(
          (aggregation) => !aggregation.hideByVisualizationTypes?.includes(visualizationType),
        );
        result[categoryId][visualizationType] = {
          items: filteredPrimaryAggregations,
        };
      }
    });
  });

  return result;
}

/**
 * Get secondary breakdown options for a specific primary aggregation
 */
export function getSecondaryBreakdownOptions(
  breakdownOptions: BreakdownOptions | null | undefined,
  primaryAggregation: string | null | undefined,
): string[] {
  if (!breakdownOptions || typeof breakdownOptions !== 'object' || !primaryAggregation) {
    return [];
  }

  const primaryConfig = breakdownOptions[primaryAggregation];

  if (!primaryConfig || !primaryConfig.secondary_breakdown_options) {
    return [];
  }

  return primaryConfig.secondary_breakdown_options;
}

/**
 * Build secondary breakdown options from backend breakdown options
 * Maps backend secondary keys to full enum objects
 */
export function buildSecondaryBreakdownOptions(
  secondaryBreakdownOptions: string[] | null | undefined,
): AggregationOption[] {
  if (!secondaryBreakdownOptions) {
    return [];
  }

  // If only NONE is available, return just NONE
  if (secondaryBreakdownOptions.length === 1 && secondaryBreakdownOptions[0] === 'NONE') {
    return [BASE_BREAKDOWN_OPTIONS.NONE];
  }

  // removes the option if it is hidden by the visualization type. e.g. LINE_CHART and GRAPH
  const filteredOptions = secondaryBreakdownOptions
    .map((option) => SECONDARY_BREAKDOWN_MAP[option] ?? null)
    .filter(Boolean);

  return filteredOptions;
}
